import {
  Typography,
  Card,
  Stack,
  Box,
  ListItemAvatar,
  Avatar,
  Divider,
  Button,
} from '@mui/material';
import palette from '../assets/sass/palette.scss';
import ProfileImage from '../components/ProfileImage';
import competitiveGameBg from '../assets/images/bg-game-card-purple.png';
import friendlyGameBg from '../assets/images/bg-game-card-green.png';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { useFirebase } from '../contexts/FirebaseContext';
import dayjs from 'dayjs';
import GamePlayerInfo from './GamePlayerInfo';
import AddIcon from '@mui/icons-material/Add';

// TODO: Refactor this component to be less monolithic and reduce Firebase coupling for better unit/component testing
// Consider breaking into:
// 1. GameCard (presentational component) - handles layout and styling
// 2. GameData hook (useGameData) - handles Firebase operations and data fetching
// 3. GamePlayerList component - handles player display logic and avatar rendering
// 4. GameDateDisplay component - handles date formatting and poll display
// 5. GameActions component - handles click handlers and navigation
// This would enable proper unit testing of business logic and component rendering separately
// Player display constants
export const PLAYER_DISPLAY_CONFIG = {
  MAX_VISIBLE_PLAYERS: 4,
  SLICE_START_INDEX: 1,
  SLICE_END_INDEX: 4,
  PLAYER_LIMIT_THRESHOLD_HIGH: 3,
  PLAYER_LIMIT_THRESHOLD_MID: 2,
  PLAYER_LIMIT_FALLBACK: 1,
};

export const getPlayerLimit = (invitations) => {
  if (!invitations) {
    return PLAYER_DISPLAY_CONFIG.PLAYER_LIMIT_FALLBACK;
  }
  if (invitations.length > PLAYER_DISPLAY_CONFIG.PLAYER_LIMIT_THRESHOLD_HIGH) {
    return PLAYER_DISPLAY_CONFIG.PLAYER_LIMIT_FALLBACK;
  }
  if (invitations.length > PLAYER_DISPLAY_CONFIG.PLAYER_LIMIT_THRESHOLD_MID) {
    return PLAYER_DISPLAY_CONFIG.PLAYER_LIMIT_THRESHOLD_HIGH;
  }
    return invitations.length;
};

const Game = ({ data, onLocationClick = null }) => {
  const { db } = useFirebase();
  const navigate = useNavigate();
  const [_invitees] = useState([...(data.invitations || [])]);

  const getUserInitial = (invitee) => {
    return invitee.userName?.charAt(0);
  };
  const invitees = () => {
    if (data.invitations) {
      return data.invitations.map((invitation) => {
        return (
          <ListItemAvatar variant="inviteAvatar" key={invitation.id}>
            <ProfileImage size="48" />
          </ListItemAvatar>
        );
      });
    }
    return [];
  };

  const [inviteesWithPhotoURL, setInviteesWithPhotoURL] = useState([]);
  console.log('inviteesWithPhotoURL', data.name, inviteesWithPhotoURL);
  useEffect(() => {
    const inviteesWithPhotoURL = async () => {
      const _inviteesWithOrganiser = [{ user: data.createdBy }, ..._invitees];
      const invitees = await Promise.all(
        _inviteesWithOrganiser.map(async (invitee) => {
          const userRef = doc(db, 'users', invitee?.user);
          const userDoc = await getDoc(userRef);
          const userData = userDoc.exists ? userDoc.data() : {};
          return {
            ...invitee,
            photoURL: userData?.profileImage || null,
            ...userData,
          };
        })
      );
      setInviteesWithPhotoURL(invitees);
    };
    inviteesWithPhotoURL();
  }, [_invitees, data.createdBy, data.players, db]);

  const getUserPhotoURL = (uid) => {
    const user = inviteesWithPhotoURL?.find((user) => user?.uid === uid || user?.user === uid);
    return user ? user?.photoURL : null;
  };

  const gameDateObject = new Date((data.date || new Date()).seconds * 1000);

  const gameFullDateString = () => {
    const dateTimeOptions = data?.dateTimeOptions;
    const firstDateTimeOption = dateTimeOptions?.[0]?.option;
    const moreThanOneOption = dateTimeOptions?.length > 1;
    // console.log('firstDateTimeOption', firstDateTimeOption);
    const optionsString = moreThanOneOption ? `(+${dateTimeOptions?.length})` : '';
    if (!data.date) {
      return (
        <>
          <Typography component="span" sx={{ color: 'green', fontWeight: 'bold' }}>
            {`Poll: `}
            {moreThanOneOption}
          </Typography>
          {`${dayjs(firstDateTimeOption?.toDate()).format(
            'dddd D MMM [at] HH:mm'
          )} ${optionsString}`}
        </>
      );
    }
    return dayjs(gameDateObject).format('dddd D MMM [at] HH:mm');
  };

  const gameBg = data.competitive ? competitiveGameBg : friendlyGameBg;

  const handleClickGame = () => {
    navigate(`/games/${data.id}`);
  };

  return (
    <Card variant="gameCard">
      <Stack
        direction="column"
        spacing={2}
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          width: '100%',
        }}
        onClick={handleClickGame}
      >
        <Stack sx={{ width: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              width: '100%',
              height: '124px',
              px: 2,
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '124px',
                backgroundColor: palette.primary,
                backgroundImage: `url(${gameBg})`,
                backgroundSize: 'cover',
                filter: 'brightness(0.75)',
                zIndex: 0,
              }}
            ></Box>
            <Typography
              variant="moduleHeader"
              onClick={(e) => (onLocationClick ? onLocationClick(data) : handleClickGame(e))}
              sx={{
                color: '#fff',
                fontSize: '18px',
                mb: 0,
                '&:hover': {
                  textDecoration: 'underline',
                },
                zIndex: 1,
              }}
            >
              {data.name}
            </Typography>
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                backgroundColor: data.competitive ? palette.primary : palette.mediumLime,
                fontWeight: 'bold',
                color: 'white',
                fontSize: '12px',
                padding: '6px 24px 6px 32px',
                borderBottomRightRadius: '16px',
              }}
            >
              <Typography component="strong" variant="factorA" className="flipped">
                W
              </Typography>{' '}
              {data.competitive ? 'Competitive' : 'Friendly'}
            </Box>
          </Box>
          {/* {data.visibility === 'Private' && (
            <Box
              sx={{
                position: 'absolute',
                top: '12px',
                left: '12px',
                width: '32px',
                height: '32px',
                backgroundColor: 'white',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: '.8',
              }}
            >
              <img src={iconHidden} alt="private" />
            </Box>
          )} */}
        </Stack>
        <Stack spacing={1} sx={{ flexGrow: '1', paddingX: 2, width: '100%' }}>
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            {gameFullDateString()}
          </Typography>

          <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
            <Typography sx={{ fontSize: '12px' }}>
              <Typography color="grey" component="span" sx={{ fontSize: '12px' }}>
                Where:
              </Typography>{' '}
              <Typography component="strong" variant="medium">
                {data.gameType}
              </Typography>
            </Typography>
            {data.cost ? (
              <Typography sx={{ fontSize: '12px' }}>
                <Typography color="grey" component="span" sx={{ fontSize: '12px' }}>
                  Cost:
                </Typography>{' '}
                <Typography component="strong" variant="medium">
                  &#0163;{data.cost}
                </Typography>
              </Typography>
            ) : null}
          </Stack>
        </Stack>
        <Divider
          sx={{
            borderColor: palette.borderPink,
            borderBottomWidth: '1px',
            width: 'calc(100% - 32px)',
          }}
        />
        <Stack
          direction="row"
          spacing={1}
          sx={{
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            width: '100%',
            px: 2,
          }}
        >
          {invitees.length > 0 && invitees()}
          {inviteesWithPhotoURL[0]?.user && (
            <Box key={`primary-invitee-${0}`}>
              <GamePlayerInfo
                player={inviteesWithPhotoURL[0]}
                index={0}
                photoUrl={getUserPhotoURL(inviteesWithPhotoURL[0]?.user) || null}
                winner={inviteesWithPhotoURL[0]?.didWin}
                size={{ width: 42, height: 42, fontSize: '12px' }}
              />
            </Box>
          )}

          <Box>
            {true && (
              <>
                <Stack direction="row">
                  {inviteesWithPhotoURL?.slice(PLAYER_DISPLAY_CONFIG.SLICE_START_INDEX, PLAYER_DISPLAY_CONFIG.SLICE_END_INDEX).map((invitee, index) => {
                    return (
                      <Box sx={{ marginLeft: '-8px', textAlign: 'center' }} key={`invitee-${index}`}>
                        {getUserPhotoURL(invitee?.user) ? (
                          <Avatar
                            key={index}
                            alt={invitee.userName || invitee.userId}
                            sx={{
                              bgcolor: index % 2 === 0 ? '#E9FFEA' : '#F9EDFF',
                              width: '48px',
                              height: '50px',
                              overflow: 'hidden',
                              mb: 0.5,
                            }}
                            src={getUserPhotoURL(invitee?.user)}
                          >
                            {/* <img
                          src={getUserPhotoURL(invitee?.user)}
                          alt=""
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        /> */}
                          </Avatar>
                        ) : (
                          <Box
                            sx={{
                              bgcolor: index % 2 === 0 ? '#E9FFEA' : '#F9EDFF',
                              width: '48px',
                              height: '50px',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              alignItems: 'center',
                              borderRadius: '50%',
                              mb: 0.5,
                            }}
                          >
                            <Typography
                              variant="factorA"
                              color="primary"
                              component="small"
                              sx={{ color: '#4C4C4C' }}
                            >
                              {getUserInitial(invitee)}
                            </Typography>
                          </Box>
                        )}
                        <Typography
                          sx={{
                            fontFamily: '"FactorA Medium", arial, sans-serif',
                            fontWeight: 700,
                            fontSize: '10px',
                            textAlign: 'center',
                            color: '#4C4C4C',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '48px',
                          }}
                        >
                          {invitee.userName || invitee.userId}
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>
                {inviteesWithPhotoURL?.length > PLAYER_DISPLAY_CONFIG.MAX_VISIBLE_PLAYERS && (
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: '12px',
                        textAlign: 'center',
                      }}
                      variant="factorA"
                    >
                      +{inviteesWithPhotoURL.length - PLAYER_DISPLAY_CONFIG.MAX_VISIBLE_PLAYERS} Others
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </Box>
          <Button
            sx={{
              borderRadius: '50%',
              minWidth: '48px',
              minHeight: '48px',
              width: '48px',
              height: '48px',
              border: `2px solid ${palette.mediumPurple}`,
              color: '#B299FF',
              p: 0,
              mx: 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <AddIcon sx={{ fontSize: 32, flexShrink: 0 }} />
          </Button>
        </Stack>
        <Divider
          sx={{
            borderColor: palette.borderPink,
            borderBottomWidth: '1px',
            width: 'calc(100% - 32px)',
          }}
        />
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: 'center', justifyContent: 'flex-start', width: '100%', paddingX: 2 }}
        >
          <Avatar src="/placeholder.jpg" alt="" sx={{ width: '40px', height: '40px' }} />
          <Typography sx={{ fontSize: '12px' }}>{data.clubName}</Typography>
        </Stack>
      </Stack>
    </Card>
  );
};

export default Game;
